import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button.js';
import { PanelSection } from '../../ui/PanelSection.js';
import type { PendingChange } from './sessionHistory.js';
import type { PageEditorState } from './usePageEditorState.js';

// ── Cambios sin guardar ──────────────────────────────────────────────────────
//
// Antes lo único que se veía era un contador. Con cinco cambios encima, "5 sin
// guardar" no dice cuáles ni deja arrepentirse de uno solo: la única salida era
// descartar todo.
//
// La lista sale del historial consolidado, la MISMA fuente que arma las
// operaciones al guardar. Si acá aparece algo, eso es lo que se va a escribir.

type Traducir = ReturnType<typeof useTranslation>['t'];

function describir(c: PendingChange, t: Traducir): string {
  if (c.type === 'structure') {
    // Un paso de estructura no tiene "antes → después" que mostrar: lo que pasó
    // es el cambio.
    return t(`projects.editor.changeKind.${c.action ?? 'move'}`);
  }
  if (c.type === 'href' || c.type === 'tag') {
    return `${t(`projects.editor.changeKind.${c.type}`)}: ${c.original ?? '—'} → ${c.value ?? '—'}`;
  }
  if (c.type === 'class') {
    const partes: string[] = [];
    if (c.added?.length) partes.push(t('projects.editor.changes.added', { classes: c.added.join(' ') }));
    if (c.removed?.length) partes.push(t('projects.editor.changes.removed', { classes: c.removed.join(' ') }));
    return partes.join(' · ');
  }
  const antes = (c.original ?? '').replace(/<[^>]*>/g, '').trim();
  const ahora = (c.value ?? '').replace(/<[^>]*>/g, '').trim();
  return t('projects.editor.changes.textChanged', {
    before: antes.slice(0, 28) || '—',
    after: ahora.slice(0, 28) || '—',
  });
}

export function PendingChangesSection({ editor }: { editor: PageEditorState }) {
  const { t } = useTranslation();
  const { pendingChanges, canRedo, revert } = editor;

  // Deshacer y rehacer se fueron a la cabecera del panel (2026-08-31). Estaban
  // acá, o sea que aparecían recién DESPUÉS del primer cambio: un deshacer que
  // se muestra cuando ya lo necesitás llega tarde. Esta sección quedó con lo
  // suyo — la lista de qué cambió y el revertir de a uno.

  // No alcanza con "hay cambios": después de deshacer el último, la lista queda
  // vacía pero todavía se puede rehacer, y el contador tiene que decirlo.
  if (pendingChanges.length === 0 && !canRedo) return null;

  return (
    <PanelSection
      id="editor-cambios"
      title={pendingChanges.length > 0
        ? t('projects.editor.changes.title', { count: pendingChanges.length })
        : t('projects.editor.changes.none')}
    >
      {pendingChanges.map((c, i) => (
        <div
          // El índice entra en la clave porque el mismo elemento puede aparecer
          // dos veces: editar un texto, duplicar el bloque y volver a editarlo
          // son dos cambios distintos, en ese orden, y los dos se guardan.
          key={`${c.targetId}-${c.type}-${String(i)}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--evo-space-2)',
            padding: 'var(--evo-space-2)',
            border: '1px solid var(--evo-stroke)',
            borderRadius: 'var(--evo-r-sm)',
            background: 'var(--evo-bg-2)',
          }}
        >
          <span style={{
            fontSize: 'var(--evo-fs-xs)',
            color: 'var(--evo-fg-3)',
            fontFamily: 'var(--evo-font)',
            lineHeight: 1.4,
            minWidth: 0,
          }}>
            {describir(c, t)}
          </span>
          <Button variant="ghost" tone="neutral" size="sm"
            onMouseDown={e => { e.preventDefault(); }}
            onClick={() => { revert(c); }}>
            {t('projects.editor.changes.revert')}
          </Button>
        </div>
      ))}
    </PanelSection>
  );
}
